import { Component, computed, input, output } from '@angular/core';
import { Octopus, UserRights } from '@maximizer/core/shared/domain';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import { UI_PIPES } from '@maximizer/core/shared/ui/pipes';
import {
  EntryLinkService,
  OutlookStore,
} from '@maximizer/outlook/shared/data-access';
import { OpportunityCard } from '@maximizer/outlook/shared/domain';
import { OUTLOOK_SHARED_UI } from '@maximizer/outlook/shared/ui';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_TOOLTIP } from '@progress/kendo-angular-tooltip';

@Component({
  selector: 'maximizer-outlook-opportunity-card',
  templateUrl: './opportunity-card.component.html',
  imports: [
    KENDO_BUTTON,
    KENDO_TOOLTIP,
    OUTLOOK_SHARED_UI,
    TranslatePipe,
    UI_KENDO,
    UI_PIPES,
  ],
})
export class OpportunityCardComponent {
  protected readonly OpportunityStatus = Octopus.OpportunityStatus;

  save = output<string>();

  userRights = input<UserRights>();
  mode = input<'read' | 'compose'>('read');
  opportunity = input<OpportunityCard>({
    key: '',
    objective: '',
    parentKey: '',
    status: Octopus.OpportunityStatus.InProgress,
    abEntry: {
      key: null,
      companyName: '',
    },
    currentStage: null,
    startDate: null,
    closeDate: null,
    owner: {
      value: '',
      display: '',
    },
    isSaved: false,
  });

  deepLink = computed(() =>
    this.entryLinkService.buildEntryLink(this.opportunity().key, 'opportunity'),
  );

  constructor(
    private readonly translate: TranslateService,
    public entryLinkService: EntryLinkService,
    public outlookStore: OutlookStore,
  ) {}

  // Compose saves the current draft, so the same opportunity can be saved again
  get isSaveDisabled(): boolean {
    return (
      (this.mode() === 'read' && this.opportunity().isSaved) ||
      !this.userRights()?.opportunities?.update ||
      !this.userRights()?.documents?.insert
    );
  }

  get saveIconClass(): string {
    if (this.opportunity().isSaved) {
      // The svg-icons asset carries its own grey; the font glyph inherits the button colour
      return this.mode() === 'compose'
        ? 'maximizer-icons icon-saved-email icon-20'
        : 'svg-icons icon-saved-email text-neutral-40 icon-20';
    }

    return this.isSaveDisabled
      ? 'maximizer-icons icon-save-email text-neutral-40 icon-24'
      : 'maximizer-icons icon-save-email icon-24';
  }

  get saveTooltipMessage(): string {
    if (this.opportunity().isSaved && this.mode() === 'read') {
      return this.translate.instant('outlook.main.tooltip.emailSavedAlt');
    }

    if (!this.userRights()?.opportunities?.update) {
      return this.translate.instant('outlook.rights.defaultMessage', {
        right: this.translate.instant('outlook.rights.edit'),
        entry: this.translate.instant('outlook.entries.opportunities'),
      });
    }

    if (!this.userRights()?.documents?.insert) {
      return this.translate.instant('outlook.rights.defaultMessage', {
        right: this.translate.instant('outlook.rights.create'),
        entry: this.translate
          .instant('outlook.entries.emailLogs')
          .toLowerCase(),
      });
    }

    return this.translate.instant('outlook.main.related.saveEmail', {
      entry: this.translate.instant('outlook.entries.opportunity'),
    });
  }
}
